import { useEffect, useState } from "react";
import AccessManagementNav from "@/components/AccessManagementNav";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PAGE_PERMISSIONS, ROLE_LABELS } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Loader2, Save, Shield, Info } from "lucide-react";

const EDITABLE_ROLES = ["institution_admin", "teacher"];

export default function Roles() {
  const [pages, setPages] = useState(PAGE_PERMISSIONS);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/roles/permissions");
      setPages(data.pages || PAGE_PERMISSIONS);
      setRoles(data.roles || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (role, key) => {
    setRoles((prev) => {
      const current = prev[role] || [];
      const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
      return { ...prev, [role]: next };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/roles/permissions", { roles });
      setRoles(data.roles || {});
      toast.success("Rol yetkileri kaydedildi ✓");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="roles-page">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Erişim Yönetimi</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Rol Yetkileri</h1>
          </div>
          <Button onClick={save} disabled={saving} className="h-11 rounded-xl bg-violet-600 px-6 font-semibold text-white hover:bg-violet-700">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Kaydet</>}
          </Button>
        </header>

        <AccessManagementNav />

        <Card className="mb-6 rounded-2xl border-violet-200/60 bg-violet-50/50 p-4">
          <div className="flex gap-3">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-violet-600" />
            <div className="text-sm text-slate-600">
              <p><strong>Süper Admin</strong> tüm sayfalara her zaman erişebilir. Burada <strong>Kurum Yöneticisi</strong> ve <strong>Öğretmen</strong> rollerinin hangi menüleri (Rol Yetkileri ve Paketler dahil) görebileceğini ayarlayabilirsiniz.</p>
            </div>
          </div>
        </Card>

        <Card className="overflow-hidden rounded-2xl border-slate-200/60 shadow-[0_2px_10px_rgba(0,0,0,0.04)]">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/80">
                  <th className="px-5 py-4 text-left font-semibold text-slate-700">
                    <span className="flex items-center gap-2"><Shield className="h-4 w-4 text-violet-600" /> Sayfa / Modül</span>
                  </th>
                  {EDITABLE_ROLES.map((role) => (
                    <th key={role} className="px-5 py-4 text-center font-semibold text-slate-700">{ROLE_LABELS[role]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pages.map((page, idx) => (
                  <tr key={page.key} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50/40"}>
                    <td className="px-5 py-3.5 font-medium text-slate-800">{page.label}</td>
                    {EDITABLE_ROLES.map((role) => {
                      const checked = (roles[role] || []).includes(page.key);
                      return (
                        <td key={role} className="px-5 py-3.5 text-center">
                          <Switch
                            checked={checked}
                            onCheckedChange={() => toggle(role, page.key)}
                            data-testid={`role-${role}-${page.key}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
